import { useState } from 'react'
import { ApiError } from '@/api/client'
import { quotaTiers as api } from '@/api/endpoints'
import type { Team } from '@/api/types'
import { Err } from '@/components/Err'
import { Button } from '@/components/ui/button'
import { DialogFooter, DialogHeader, DialogTitle } from '@/components/ui/dialog'
import { Label } from '@/components/ui/label'
import { ToneBadge } from '@/components/ToneBadge'
import { usePicked } from '@/hooks/usePicked'
import { usePoll } from '@/hooks/usePoll'
import { useWorldRefresh } from '@/hooks/useWorld'
import { limitsText } from '@/domain/quota'
import { LIST, PeoplePicker, ROW } from './PeoplePicker'
import type { Candidate } from './teamPick'

// `직위 관리`(F-72, F-124) — 여러 사람을 직위 하나에 넣는다. 대화 상자의 본문만이고 `<Dialog>` 는 부르는 쪽
// (사용자 목록의 `UserActions`, 직위 목록, 직위 상세의 `사용자 추가`)이 갖는다.
// **적용해도 창은 남는다**(F-124): 된 사람은 고른 칸에서 빠지고 직위는 고른 채라 다음 사람을 골라 이어서 적용한다.
// 무엇이 됐는지는 한 줄로 말하고, 닫기는 `닫기` 로만 한다.
// 왼쪽 칸은 `팀 관리` 와 같은 `PeoplePicker` 다 — 검색은 직위 이름도 본다.
//
// **고르는 것이라 합칠 산수가 없다.** 사람마다 다섯 숫자를 적던 때는 "적은 칸만 바꾸고 비운 칸은
// 그대로" 를 지키려고 보내기 직전에 그 사람의 지금 값을 읽어야 했다. 직위는 id 하나를 보내므로
// 그 읽기도, 빈 칸에 두 뜻이 얹히는 위험도 없다 — 빈 칸은 직위 안에서만 뜻을 갖고 거기서는 자유다.
//
// **사람 줄은 각자의 지금 직위를 보인다** — 이미 그 직위인 사람을 가려내지 못하면 같은 적용을 되풀이한다.
// 보이는 것은 직위 이름 하나다: 한도는 고르는 쪽 목록이 말하고, 한 사람의 사용량은 사용자 상세가 말한다.
//
// **적용은 몇 명씩 함께 보낸다** — 한 명씩 기다리면 고른 사람 수만큼 왕복이 쌓인다. 실패한 사람만 남고
// 첫 오류를 말하는 것은 한 명씩 보낼 때와 같다. 끝나면 실패가 있어도 목록을 곧바로 다시 읽는다 — 된 사람의
// 직위가 다음 폴링까지 옛 이름으로 서지 않게.
//
// **탈퇴한 사람은 넣은 수에 세지 않는다** — 서버는 그 쓰기를 409 `already_withdrawn` 으로 거절한다. 그 사람은 고른 칸에서
// 빼고 한 줄로 말한다(권한·팀 창과 같다). 고르는 자리의 찾기에 없는 사람이라 남겨 두면 풀 길 없이 적용마다 같은 거절이다.

const POLL_MS = 30000
// 한 번에 함께 보내는 적용의 수.
const SEND_AT_ONCE = 4

export function QuotaPanel({
  seed,
  teams,
  tier: seedTier = null,
  onDone,
  onApplied,
}: {
  /** 골라진 채 열 사람 — 사용자 목록에서 체크한 줄. */
  seed: Candidate[]
  /** 찾은 사람의 소속을 잇는 팀 목록(`World` 의 것). */
  teams: Team[]
  /** 미리 골라 둘 직위 — 직위 상세의 `사용자 추가` 가 그 직위로 연다. */
  tier?: string | null
  /** 닫기. */
  onDone: () => void
  /** 적용이 한 번 끝날 때마다 — 부르는 쪽의 목록(인원)을 다시 읽게. */
  onApplied?: () => void
}) {
  const tiers = usePoll(() => api.list(), POLL_MS)
  const who = usePicked(seed)
  const [tier, setTier] = useState<string | null>(seedTier)
  const [busy, setBusy] = useState(false)
  const [error, setError] = useState<unknown>(null)
  const [applied, setApplied] = useState<string | null>(null)
  const [dropped, setDropped] = useState<string[]>([])
  const refreshWorld = useWorldRefresh()

  const chosen = who.chosen
  const rows = (tiers.data ?? []).slice().sort((a, b) => a.sort_order - b.sort_order || a.display_name.localeCompare(b.display_name))

  const save = async () => {
    if (tier === null) return
    setBusy(true)
    setError(null)
    setApplied(null)
    setDropped([])
    const errors = new Map<string, unknown>()
    const gone = new Set<string>()
    const queue = chosen.map((p) => p.user.id)
    const send = async () => {
      for (let id = queue.shift(); id !== undefined; id = queue.shift()) {
        try {
          await api.put(id, tier)
        } catch (e) {
          if (e instanceof ApiError && e.reason === 'already_withdrawn') gone.add(id)
          else errors.set(id, e)
        }
      }
    }
    await Promise.all(Array.from({ length: Math.min(SEND_AT_ONCE, queue.length) }, send))
    setBusy(false)
    refreshWorld()
    tiers.refresh()
    onApplied?.()
    const done = chosen.length - errors.size - gone.size
    const name = rows.find((t) => t.id === tier)?.display_name ?? '직위'
    if (done > 0) setApplied(`${done}명을 ${name}에 넣었습니다.`)
    setDropped(chosen.filter((p) => gone.has(p.user.id)).map((p) => p.user.username))
    // 된 사람은 빠지고 안 된 사람만 남는다 — 고른 직위는 그대로라 같은 버튼이 곧 재시도이고, 다음 사람을 골라 이어서 할 수 있다.
    who.only(new Set(errors.keys()))
    // 첫 오류는 고른 순서로 앞선 사람의 것이다 — 함께 보내도 먼저 답한 쪽이 아니다.
    const first = chosen.find((p) => errors.has(p.user.id))
    if (first !== undefined) setError(errors.get(first.user.id))
  }

  const ready = chosen.length > 0 && tier !== null

  return (
    <>
      <DialogHeader>
        <DialogTitle>직위 관리</DialogTitle>
      </DialogHeader>

      <div className="grid grid-cols-2 gap-4 max-sm:grid-cols-1">
        <PeoplePicker
          id="quota-users"
          teams={teams}
          chosen={chosen}
          onMember={who.set}
          onFound={who.renew}
          aside={(p) => p.user.quota_tier?.display_name}
        />

        <div className="grid content-start gap-2">
          <Label id="quota-tier">직위</Label>
          <Err error={tiers.error} />
          <ul aria-labelledby="quota-tier" className={LIST}>
            {rows.length === 0 ? (
              <li className="px-3 py-2 text-[12px] text-muted-foreground">{tiers.loading ? '불러오는 중' : '직위 없음'}</li>
            ) : (
              rows.map((t) => (
                <li key={t.id}>
                  <label className={`${ROW} items-start`}>
                    <input
                      type="radio"
                      name="quota-tier-pick"
                      aria-label={`${t.display_name} 고르기`}
                      className="mt-1 shrink-0"
                      value={t.id}
                      checked={tier === t.id}
                      disabled={busy}
                      onChange={() => setTier(t.id)}
                    />
                    <span className="min-w-0 flex-1">
                      <span className="flex flex-wrap items-center gap-2 font-medium">
                        {t.display_name}
                        {t.is_default ? <ToneBadge tone="ok">기본</ToneBadge> : null}
                      </span>
                      {/* 어느 직위를 고를지는 그 직위의 숫자로 정한다 — 이름만으로는 정할 수 없다. */}
                      <span className="mt-0.5 block text-[11px] text-muted-foreground">{limitsText(t.limits)}</span>
                    </span>
                  </label>
                </li>
              ))
            )}
          </ul>
          <div className="flex justify-end">
            <Button type="button" size="sm" disabled={!ready || busy} onClick={() => void save()}>
              {busy ? '적용 중' : '적용'}
            </Button>
          </div>
        </div>
      </div>

      {applied ? <p role="status" className="text-[13px] text-ok-ink">{applied}</p> : null}
      {dropped.length > 0 ? (
        <p role="status" className="text-[13px] text-warn-ink">{`탈퇴한 ${dropped.join(', ')} 의 고르기를 풀었습니다.`}</p>
      ) : null}
      <Err error={error} />
      <DialogFooter>
        <Button type="button" variant="outline" disabled={busy} onClick={onDone}>
          닫기
        </Button>
      </DialogFooter>
    </>
  )
}
